import React, { useMemo, useState } from "react";
import { useMatkaBetPlacement } from "./useMatkaBetPlacement";
import { useDispatch, useSelector } from "react-redux";
import { openLoginModal, openSignupModal } from "../../../features/slices/ModalSlice";
import { fetchMatkaSuggestions } from "../../../api/services/matka";
import { QUICK_MARKETS } from "./constants";
import MatkaFooter from "./MatkaFooter";
import MatkaSessionSelect, {
  applySessionToBids,
  getSessionOptions,
} from "./MatkaSessionSelect";

const DIGIT_ERROR = "Enter a 2-digit number (00-99)";
const POINTS_ERROR = "Points must be between 1 and 10,000.";

function isValidJodi(value) {
  return /^\d{2}$/.test(value) && Number(value) >= 0 && Number(value) <= 99;
}

export default function MatkaRedBracket({
  selectedMarket,
  selectedGameType,
  onBackToGameTypes,
  onBackToMarkets,
  onQuickMarketSelect,
  onSelectTab,
  onPlaceBet,
}) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);

  const [gameType, setGameType] = useState(() =>
    getSessionOptions(selectedGameType?.betDirection)[0].toLowerCase()
  );
  const [redBracket, setRedBracket] = useState("");
  const [enterPoints, setEnterPoints] = useState("");
  const [digitError, setDigitError] = useState("");
  const [pointsError, setPointsError] = useState("");
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(false);
  const [bids, setBids] = useState([]);
  const { openReview, betModals } = useMatkaBetPlacement({
    market: selectedMarket,
    gameType: selectedGameType,
    session: gameType,
    bids,
    onPlaced: () => setBids([]),
  });

  const totalBids = bids.length;
  const totalPoints = useMemo(
    () => bids.reduce((sum, bid) => sum + (Number(bid.points) || 0), 0),
    [bids]
  );

  const handleDigitChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 2);
    setRedBracket(next);
    setFormError("");
    setDigitError(next.length === 1 ? DIGIT_ERROR : "");
  };

  const handlePointsChange = (event) => {
    const next = event.target.value.replace(/\D/g, "").slice(0, 5);
    setEnterPoints(next);
    setFormError("");
    if (next === "") {
      setPointsError("");
      return;
    }
    const value = parseInt(next, 10);
    setPointsError(value >= 1 && value <= 10000 ? "" : POINTS_ERROR);
  };

  const handleAddBid = async (event) => {
    if (event && event.preventDefault) event.preventDefault();
    if (loading) return;

    if (redBracket.length !== 2) {
      setDigitError(DIGIT_ERROR);
      return;
    }

    if (enterPoints.trim() === "") {
      setNotice("Please fill all fields");
      return;
    }

    const pointsValue = parseInt(enterPoints, 10);
    if (!Number.isInteger(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
      setPointsError(POINTS_ERROR);
      return;
    }

    setLoading(true);
    setFormError("");
    try {
      const data = await fetchMatkaSuggestions("red_bracket", redBracket);
      const raw = Array.isArray(data?.jodis) ? data.jodis.map(String) : [];
      if (raw.length === 0) {
        setFormError("No suggestions available. Please try again.");
        return;
      }

      const jodis = raw.filter(isValidJodi);
      if (jodis.length === 0) {
        setFormError("No valid suggestions. Please try again.");
        return;
      }

      setBids((currentBids) => {
        const consumed = new Set();
        const nextRows = jodis.map((jodi, index) => {
          const existing = currentBids.find(
            (bid) =>
              bid.jodi === jodi && bid.gameType === gameType && !consumed.has(bid.id)
          );
          if (existing) {
            consumed.add(existing.id);
            return { ...existing, points: Number(existing.points) + pointsValue };
          }
          return {
            id: Date.now() + index,
            jodi,
            gameType,
            points: pointsValue,
          };
        });
        const rest = currentBids.filter((bid) => !consumed.has(bid.id));
        return [...nextRows, ...rest];
      });
      setRedBracket("");
      setEnterPoints("");
      setDigitError("");
      setPointsError("");
    } catch (error) {
      setFormError(error?.message || "Unable to load suggestions.");
    } finally {
      setLoading(false);
    }
  };

  const handleGameTypeChange = (nextGameType) => {
    setGameType(nextGameType);
    setBids((currentBids) =>
      applySessionToBids(currentBids, nextGameType, (bid) => bid.jodi)
    );
  };

  const handleDeleteBid = (bidId) => {
    setBids((currentBids) => currentBids.filter((bid) => bid.id !== bidId));
  };

  const handleContinue = () => {
    if (!totalBids) return;
    if (!user?.userLoggedIn) {
      dispatch(openLoginModal("login"));
      return;
    }
    openReview();
  };

  return (
    <div className="matka-betting-screen-wrapper">
      {/* Top Header */}
      <header className="matka-betting-header">
        <div className="container-fluid custom-padding d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          {/* Brand Logo & ONLINE badge */}
          <div className="d-flex align-items-center gap-2">
            <span
              className="matka-betting-brand-text"
              onClick={onBackToMarkets}
            >
              Matka
            </span>
            <span className="matka-online-badge">ONLINE</span>
          </div>

          {/* Quick Market Switcher */}
          <div className="matka-quick-market-switcher d-flex align-items-center">
            {QUICK_MARKETS.map((qm, idx) => (
              <React.Fragment key={qm.id}>
                <button
                  type="button"
                  className={`matka-quick-market-btn ${
                    selectedMarket?.id === qm.id ? "active" : ""
                  }`}
                  onClick={() => onQuickMarketSelect(qm.id)}
                >
                  {qm.icon && <span className="me-1">{qm.icon}</span>}
                  {qm.name}
                </button>
                {idx < QUICK_MARKETS.length - 1 && (
                  <span className="matka-switcher-dot">•</span>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* User Auth Buttons */}
          <div className="d-flex align-items-center gap-2">
            {!user?.userLoggedIn ? (
              <>
                <button
                  type="button"
                  className="matka-betting-login-btn"
                  onClick={() => dispatch(openLoginModal("login"))}
                >
                  LOGIN
                </button>
                <button
                  type="button"
                  className="matka-betting-signup-btn"
                  onClick={() => dispatch(openSignupModal())}
                >
                  SIGN UP
                </button>
              </>
            ) : (
              <span className="matka-user-logged-badge">
                {user?.user?.name || user?.user?.username || "Player"}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Main Body */}
      <main className="matka-betting-body pt-3 pb-4">
        <div className="matka-main-centered-container">
          {/* Back to Market Link */}
          <button
            type="button"
            className="matka-back-to-market-link d-inline-flex align-items-center mb-2"
            onClick={onBackToGameTypes}
          >
            <i className="bi bi-chevron-left me-1"></i>
            Back to{" "}
            {selectedMarket?.name
              ? selectedMarket.name.charAt(0) +
                selectedMarket.name.slice(1).toLowerCase()
              : "Mahadevi"}
          </button>

          {/* Screen Title */}
          <h1 className="matka-panna-bulk-title mb-4">Red Bracket</h1>
          <div className="mb-4">
            <label className="matka-panna-bulk-label">GAME TYPE</label>
            <MatkaSessionSelect
              betDirection={selectedGameType?.betDirection}
              variant="bulk"
              value={gameType}
              onChange={handleGameTypeChange}
            />
          </div>

          {/* Top Row: RED BRACKET and ENTER POINTS side-by-side */}
          <div className="row g-3 mb-2">
            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">RED BRACKET</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="83"
                value={redBracket}
                onChange={handleDigitChange}
              />
              {digitError && <p className="matka-sangam-error">{digitError}</p>}
            </div>

            <div className="col-12 col-md-6">
              <label className="matka-panna-bulk-label">ENTER POINTS</label>
              <input
                type="text"
                inputMode="numeric"
                className="matka-panna-bulk-input"
                placeholder="Enter Points (e.g. 100)"
                value={enterPoints}
                onChange={handlePointsChange}
              />
              {pointsError && <p className="matka-sangam-error">{pointsError}</p>}
            </div>
          </div>

          {formError && <p className="matka-sangam-error mb-3">{formError}</p>}

          {/* Add Bid Button (Right-Aligned) */}
          <div className="d-flex justify-content-end mb-4">
            <button
              type="button"
              className="matka-add-bid-btn"
              onClick={handleAddBid}
              disabled={loading}
            >
              {loading && (
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
              )}
              ADD BID
            </button>
          </div>

          {/* Current Bids List Card */}
          <div className="matka-bids-list-card mb-4">
            <h3 className="matka-bids-list-title">Current Bids List</h3>
            <div className="table-responsive">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th className="th-panna">Jodi</th>
                    <th className="th-type text-center">Game Type</th>
                    <th className="th-points text-end">Points (₹)</th>
                    <th className="th-action" aria-label="Delete"></th>
                  </tr>
                </thead>
                <tbody>
                  {bids.map((bid) => (
                    <tr key={bid.id}>
                      <td className="td-panna">{bid.jodi}</td>
                      <td className="td-type text-center">{bid.gameType}</td>
                      <td className="td-points text-end">{bid.points}</td>
                      <td className="td-action text-end">
                        <button
                          type="button"
                          className="matka-jodi-delete-btn"
                          onClick={() => handleDeleteBid(bid.id)}
                          aria-label={`Delete ${bid.jodi}`}
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* In-page Action Bar with Bids, Points, and CONTINUE */}
          <div className="matka-bulk-summary-bar">
            <div className="d-flex align-items-center gap-3">
              {/* Bids Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">{totalBids}</span>
                <span className="matka-bulk-counter-label">Bids</span>
              </div>

              {/* Total Points Counter */}
              <div className="matka-bulk-counter-pill">
                <span className="matka-bulk-counter-val">
                  {totalPoints.toLocaleString()}
                </span>
                <span className="matka-bulk-counter-label">Total Points</span>
              </div>
            </div>

            {/* Continue Action Button */}
            <button
              type="button"
              className="matka-bulk-continue-btn"
              onClick={handleContinue}
              disabled={totalBids === 0}
            >
              CONTINUE
            </button>
          </div>
        </div>
      </main>

      {notice && (
        <div className="matka-sangam-popup" role="dialog" aria-modal="true">
          <div className="matka-sangam-popup-box">
            <p>{notice}</p>
            <button type="button" onClick={() => setNotice("")}>
              OK
            </button>
          </div>
        </div>
      )}

      {betModals}

      {/* Matka Footer */}
      <MatkaFooter
        onSelectTab={onSelectTab}
        onBackToMarkets={onBackToMarkets}
      />
    </div>
  );
}
